import { LayoutGrid, Rows, Save, TrendingUp } from 'lucide-react'
import { useCallback, useRef } from 'react'
import useBracketStore from '@/stores/useBracketStore'
import { renderLeaderboardShareCard, SHARE_CARD_VARIANTS } from '../../lib/shareCard'
import { getShareUrl } from '../../lib/shareUrl'
import { AnimatedPresence } from '../ui/animated-presence'
import { Avatar } from '../ui/avatar'
import { Button } from '../ui/button'
import EntryStatsRow from '../EntryStatsRow'
import ShareButton from '../ShareButton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'

function getChampionLabel(status) {
  if (!status || status === 'not_selected') return null
  if (status === 'active') return 'Active'
  if (status === 'won') return 'Won'
  if (status === 'eliminated') return 'Eliminated'
  return null
}

export default function BracketViewToggleBar() {
  const viewMode = useBracketStore(s => s.viewMode)
  const setViewMode = useBracketStore(s => s.setViewMode)
  const viewingLeaderboard = useBracketStore(s => s.viewingLeaderboard)
  const setViewingLeaderboard = useBracketStore(s => s.setViewingLeaderboard)
  const leaderboardGroupFilter = useBracketStore(s => s.leaderboardGroupFilter)
  const setLeaderboardGroupFilter = useBracketStore(s => s.setLeaderboardGroupFilter)
  const userGroups = useBracketStore(s => s.userGroups)
  const canEditPicks = useBracketStore(s => s.canEditPicks)
  const isAdmin = useBracketStore(s => s.isAdmin)
  const isViewingOfficial = useBracketStore(s => s.isViewingOfficial)
  const isOwnEntry = useBracketStore(s => s.isOwnEntry)
  const isViewingEntry = useBracketStore(s => s.isViewingEntry)
  const isNewEntryMode = useBracketStore(s => s.isNewEntryMode)
  const userEntry = useBracketStore(s => s.userEntry)
  const liveStats = useBracketStore(s => s.liveStats)
  const hasPicksChanged = useBracketStore(s => s.hasPicksChanged)
  const userSaveLabel = useBracketStore(s => s.userSaveLabel)
  const handleSave = useBracketStore(s => s.handleSave)
  const tournament = useBracketStore(s => s.tournament)
  const bracket = useBracketStore(s => s.bracket)
  const leaderboard = useBracketStore(s => s.leaderboard)
  const leaderboardLoading = useBracketStore(s => s.leaderboardLoading)
  const setInlineSaveInView = useBracketStore(s => s.setInlineSaveInView)
  const entryOwnerProfile = useBracketStore(s => s.entryOwnerProfile)
  const navigate = useBracketStore(s => s._navigate)

  // Report whether the inline Save button ITSELF is on screen — not the whole
  // bar: on mobile the bar wraps to two rows, and observing the bar left a
  // dead zone where the button (row 1) was scrolled off while the stats
  // (row 2) still intersected, so neither save control was visible. The
  // floating Save FAB in BracketPicks shows only while this is false, so the
  // user sees a single Save button that relocates to the bottom-right corner
  // the moment the inline one leaves view. Callback ref because the button
  // mounts/unmounts with AnimatedPresence.
  //
  // The top of the visibility box is pulled DOWN by the sticky BracketHeader's
  // height (published as --bracket-header-offset) — the button slides UNDER
  // that bar and is hidden to the user well before it exits the viewport, so
  // judging by the raw viewport left a dead zone with no save control.
  const observerRef = useRef(null)
  const inlineSaveRef = useCallback((el) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInlineSaveInView(true)
      return
    }
    const headerOffset = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--bracket-header-offset'),
      10,
    ) || 0
    observerRef.current = new IntersectionObserver(([entry]) => {
      setInlineSaveInView(entry.isIntersecting)
    }, { rootMargin: `-${headerOffset}px 0px 0px 0px` })
    observerRef.current.observe(el)
  }, [setInlineSaveInView])

  // Stats are only meaningful when viewing an entry (existing or new)
  const showEntryStats = isViewingEntry && (userEntry || isNewEntryMode) && !viewingLeaderboard
  const championStatus = isNewEntryMode
    ? (liveStats.hasChampionPick ? 'active' : null)
    : userEntry?.champion_status
  const championLabel = getChampionLabel(championStatus)
  // get_entry_by_id masks the name with the status, so another player's champ
  // only shows once the final round is public (ticket 8baa1d56).
  const championName = isNewEntryMode ? null : userEntry?.champion_team_name
  // liveStats recomputes from the picks on screen so the number moves while
  // YOU pick, before saving. On someone else's entry the picks on screen are
  // masked for rounds that have not closed, so the server value -- which
  // counts every round (20260819020818) -- is the only complete one.
  const displayPotentialPoints = isOwnEntry && liveStats.potentialPoints > 0
    ? liveStats.potentialPoints
    : (userEntry?.potential_points || 0)
  const displayAccuracy = userEntry?.accuracy ?? null
  const totalPoints = userEntry?.total_score || 0

  return (
    <div className="mb-4 flex items-center gap-3 flex-wrap">
      <Tabs
        value={viewingLeaderboard ? 'leaderboard' : viewMode}
        onValueChange={(val) => {
          if (val === 'leaderboard') {
            setViewingLeaderboard(true)
          } else {
            setViewingLeaderboard(false)
            setViewMode(val)
          }
        }}
        activationMode="manual"
      >
        <TabsList variant="secondaryNav">
          {/* Staggered/Compact only meaningful for the single-elim tree layout. */}
          {bracket?.format !== 'pool_then_sunday' && (
            <>
              <TabsTrigger value="staggered" variant="secondaryNav" className="hidden sm:inline-flex">
                <LayoutGrid className="h-4 w-4 mr-2" />
                Staggered
              </TabsTrigger>
              <TabsTrigger value="compact" variant="secondaryNav">
                <Rows className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Compact</span>
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="leaderboard" variant="secondaryNav">
            <TrendingUp className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Leaderboard</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Group filter -- visible when leaderboard tab is active */}
      {viewingLeaderboard && userGroups.length > 0 && (
        <Select value={leaderboardGroupFilter} onValueChange={setLeaderboardGroupFilter}>
          <SelectTrigger className="w-auto min-w-[8rem] h-8 sm:h-10">
            <SelectValue placeholder="Global" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Global</SelectItem>
            {userGroups.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* DUB-101: share button for the per-bracket leaderboard, in-row with
          the view toggles. Only present when the leaderboard tab is active
          and there are entries to render in the share-card graphic. */}
      {viewingLeaderboard && !leaderboardLoading && leaderboard.length > 0 && (
        <ShareButton
          iconOnly
          ariaLabel="Share bracket leaderboard"
          title={tournament?.name && bracket?.divisionName
            ? `${tournament.name} — ${bracket.divisionName}`
            : 'Dub bracket leaderboard'}
          text="Check out this leaderboard on dub."
          url={getShareUrl()}
          className="ml-auto h-10 w-10 p-0 rounded-full bg-background shrink-0"
          variants={SHARE_CARD_VARIANTS}
          getImage={(variantKey) => renderLeaderboardShareCard({
            title: 'Who got the dub?',
            subtitle: [tournament?.name, bracket?.divisionName, leaderboard.length > 0 ? `${leaderboard.length.toLocaleString()} entries` : null].filter(Boolean).join(' • '),
            variant: variantKey,
            rows: leaderboard.slice(0, 5).map(r => ({
              rank: r.rank,
              full_name: r.full_name,
              total_points: r.total_points,
              is_current_user: r.is_current_user,
              accuracy: r.avg_accuracy,
              potential_points: r.potential_points,
            })),
          })}
        />
      )}

      {/* Stats — right-aligned, inline with the view toggle. On mobile the
          Save button is ordered BEFORE the stats (order-1 vs order-2) so it
          shares the first row with the compact tab icons, top-right, and the
          stats wrap to a second row — instead of the stats hogging row one
          and the button dangling on a row of its own. */}
      {showEntryStats && (
        <div className="order-2 sm:order-none ml-auto flex items-center gap-3 min-w-0">
          {/* When viewing another player's entry, their avatar sits beside
              their stats and deep-links to their profile (widget request). */}
          {!isOwnEntry && userEntry?.user_id && (
            <button
              type="button"
              onClick={() => navigate?.(`/profile/${userEntry.user_id}`)}
              aria-label={entryOwnerProfile?.first_name
                ? `View ${entryOwnerProfile.first_name}'s profile`
                : "View this player's profile"}
              title={entryOwnerProfile?.first_name
                ? `View ${entryOwnerProfile.first_name}'s profile`
                : "View this player's profile"}
              className="shrink-0 rounded-full cursor-pointer transition-shadow hover:ring-2 hover:ring-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Avatar
                size="sm"
                fullName={entryOwnerProfile?.full_name || entryOwnerProfile?.first_name}
                avatarUrl={entryOwnerProfile?.avatar_url}
                avatarType={entryOwnerProfile?.avatar_type}
              />
            </button>
          )}
          <div className="flex items-baseline gap-1 whitespace-nowrap">
            <span className="text-lg sm:text-xl font-bold text-primary tabular-nums leading-none">
              {totalPoints.toLocaleString()}
            </span>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {totalPoints === 1 ? 'pt' : 'pts'}
            </span>
          </div>
          <span aria-hidden className="h-5 w-px bg-border" />
          <EntryStatsRow
            potentialPoints={displayPotentialPoints}
            accuracy={displayAccuracy}
            accuracyNullDisplay="dash"
            champion={championLabel ? { label: championLabel, status: championStatus, name: championName } : null}
            size="sm"
          />
        </div>
      )}

      {/* Save Button */}
      {canEditPicks && (isViewingOfficial ? isAdmin : isOwnEntry) && (
        <AnimatedPresence
          ref={inlineSaveRef}
          show={hasPicksChanged}
          className={`order-1 sm:order-none ml-auto ${showEntryStats ? 'sm:ml-0' : ''}`}
        >
          <Button
            onClick={handleSave}
            size="md"
          >
            <Save className="h-4 w-4 mr-2" />
            {isViewingOfficial ? 'Save Official' : userSaveLabel}
          </Button>
        </AnimatedPresence>
      )}
    </div>
  )
}
